/*
**  rui-sort-button.js
**
**  rui-sort-button component
*/
import { LitElement, html, css } from 'lit';
import { ruiCommonStyles } from '../common/rui-common-styles.js';
import { classMap } from 'lit/directives/class-map.js';
export class RuiSortButton extends LitElement {
    static get is() {
        return 'rui-sort-button';
    }
    static get properties() {
        return {
            text: { type: String },
            showArrow: { type: Object },
            arrowDirection: { type: String }
        };
    }
    constructor() {
        super();
        this.text = '';
        this.showArrow = false;
        this.arrowDirection = 'up';
        this._parentAlignment = 'left';
        this._sortDirection = 'ascending';
    }
    click(e) {
        e.preventDefault();
        e.stopPropagation();
        let clickEvent = new CustomEvent('rui-click', {
            detail: {},
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(clickEvent);
    }
    render() {
        // Work out parent cell's alignment so we can display the proper left/right
        // margin offset so the column header lines up with the column data
        if (this.parentNode.classList.contains('rui-responsive-cell-right')) {
            this._parentAlignment = 'right';
        }
        else if (this.parentNode.classList.contains('rui-responsive-cell-center')) {
            this._parentAlignment = 'center';
        }
        // Determine sort direction to assist with accessibility
        if (this.arrowDirection.toLowerCase() == 'up') {
            this._sortDirection = 'ascending';
        }
        else {
            this._sortDirection = 'descending';
        }
        const classes = {
            ['rui-sort-button']: true,
            ['rui-sort-button-center']: this._parentAlignment === 'center',
            ['rui-sort-button-right']: this._parentAlignment === 'right'
        };
        const iconClasses = {
            ['rui-sort-button-icon']: true,
            ['rui-sort-button-icon-up']: this.arrowDirection.toLowerCase() === 'up'
        };
        return html `
            <button class="${classMap(classes)}" @click="${this.click}">
                <div class="rui-sort-button-container">
                    <span class="rui-sort-button-text">${this.text}</span>
                    ${this.showArrow ? html `<span class="sr-only">sorted ${this._sortDirection}</span><rui-icon iconType="window-dropdown" context="sort-btn" aria-hidden="true" class="${classMap(iconClasses)}"></rui-icon>` : ''}
                </div>
            </button>
        `;
    }
    static get styles() {
        return [
            ruiCommonStyles,
            css `
                .rui-sort-button {
                    color: var(--rui-accent, #085ce5);
                    background: none;
                    border: none;
                    border-radius: var(--rui-btn-border-radius, 16px);
                    cursor: pointer;
                    font-family: var(--rui-font-family, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                    font-size: var(--rui-btn-font-size-md, 13px);
                    line-height: var(--rui-btn-line-height-md, 1.42857143);
                    font-weight: 600;
                    margin-left: -11px;
                    outline-style: none;
                    padding: 5px 11px;
                    transition: background 0.25s;
                    user-select: none;
                    -webkit-font-smoothing: antialiased;
                }

                @media (prefers-reduced-motion: reduce) {
                    .rui-sort-button {
                        transition: none;
                    }
                }

                .rui-sort-button-right {
                    margin-left: 0;
                    margin-right: -11px;
                }

                .rui-sort-button-center {
                    margin-left: 0;
                    margin-right: 0;
                }
                
                .rui-sort-button:hover {
                    background-color: var(--rui-btn-secondary-bg-hover, #e8edff);
                    border-color: var(--rui-btn-secondary-border, #aec8f5);
                }
                
                .rui-sort-button:focus {
                    background-color: var(--rui-btn-secondary-bg-hover, #e8edff);
                    border-color: var(--rui-btn-secondary-border, #aec8f5);
                    outline: var(--rui-outline-width, 1px) var(--rui-outline-style, solid) var(--rui-btn-secondary-border, #aec8f5);
                    outline-offset: var(--rui-outline-offset-btn, 0);
                }
                
                .rui-sort-button:active {
                    background-color: var(--rui-btn-secondary-bg-active, #d1daff);
                    border-color: var(--rui-btn-secondary-border, #aec8f5);
                    outline: var(--rui-outline-width, 1px) var(--rui-outline-style, solid) var(--rui-btn-secondary-border, #aec8f5);
                    outline-offset: var(--rui-outline-offset-btn, 0);
                }
                
                .rui-sort-button-container {
                    display: flex;
                }
                
                .rui-sort-button-text {
                    flex: 1 0 0%;
                }
                
                .rui-sort-button-icon {
                    flex: 0 0 auto;
                    margin-left: 6px;
                }
                
                .rui-sort-button-icon-up {
                    transform: rotate(-180deg);
                }
        
                @media print {
                    .rui-sort-button {
                        color: #000000;
                    }
                }
            `
        ];
    }
}
customElements.get(RuiSortButton.is) || customElements.define(RuiSortButton.is, RuiSortButton);
